import { Box, Divider, Stack, Typography } from "@mui/material";
import { LabelValue } from "@zfp-akademie-a-s/zfp-design";
import { Spacing } from "Infrastructure/Themes/Themes";
import { Resources, useResource } from "Infrastructure/Translations/Resources";
import { type NsureSelectionItem, NsureSelectionItemType } from "./InvestmentsNsureSelectionContext";
import { useInstrumentTypeLabel } from "./useInstrumentTypeLabel";
import { formatPercent, formatSrriSri } from "./utils";

type Props = {
  readonly selection: NsureSelectionItem;
};

export const InvestmentsNsureSelectionHeader = ({ selection }: Props) => {
  const { t, resolveResourcePath } = useResource();
  const getInstrumentTypeLabel = useInstrumentTypeLabel();

  // a portfolio's risk category, an instrument's type
  const subtitle =
    selection.type === NsureSelectionItemType.Portfolio
      ? selection.strategyRiskType &&
        `${t(Resources.AnalysisDetail.GoalsFulfillment.Nsure.PortfolioCard.Category)} ${t(
          resolveResourcePath(`Resources.AnalysisDetail.GoalsFulfillment.Nsure.StrategyRiskType.${selection.strategyRiskType}`),
        )}`
      : selection.instrumentType && getInstrumentTypeLabel(selection.instrumentType);

  return (
    <Stack spacing={Spacing.L}>
      <Box>
        <Typography variant="h3" lineHeight={1.3}>
          {selection.name}
        </Typography>
        {subtitle && (
          <Typography variant="caption" color="text.secondary">
            {subtitle}
          </Typography>
        )}
      </Box>

      <Divider />

      {/* the last column as wide as its label, "Očekáv. zhodnocení" on one line; the other two share the rest */}
      <Box display="grid" gridTemplateColumns="1fr 1fr auto" columnGap={Spacing.L}>
        <LabelValue
          uppercase={false}
          label={t(Resources.AnalysisDetail.GoalsFulfillment.Nsure.Labels.SrriSri)}
          value={<SelectionMetric value={formatSrriSri(selection.srri, selection.sri)} />}
        />
        <LabelValue
          uppercase={false}
          label={t(Resources.AnalysisDetail.GoalsFulfillment.Nsure.Labels.Esg)}
          value={<SelectionMetric value={formatPercent(selection.esg)} />}
        />
        <LabelValue
          uppercase={false}
          label={t(Resources.AnalysisDetail.GoalsFulfillment.Nsure.Labels.ExpectedPerformance)}
          value={<SelectionMetric value={formatPercent(selection.expectedPerformance)} />}
        />
      </Box>
    </Stack>
  );
};

// the panel's values are larger than a card's, LabelValue's own body2
const SelectionMetric = ({ value }: { readonly value: string }) => (
  <Typography variant="body1" fontWeight={700} noWrap>
    {value}
  </Typography>
);
